import React, {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {UpdateLocationCallbackType} from 'components/data/WithUrlUpdater'
import {getShowHostelsAlert} from 'modules/sapiSearch/selectors'
import {
  getIsUserClosedHostelAlert,
  getNoHostels,
  isSortAscendingByPrice
} from 'modules/search/selectors'
import {setIsUserClosedHostelAlert} from 'modules/search/slice'

import {Alert} from '@vio/atlas/Alert'
import {ButtonLink} from '@vio/atlas/ButtonLink'
import {Grid} from '@vio/atlas/helpers/Grid'
import OpacityAnimation from '@vio/atlas/helpers/OpacityAnimation'
import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

type Props = {
  onChange: UpdateLocationCallbackType
}

type HostelMessageProps = {
  onClick: (event: React.MouseEvent) => void
}

type MessageLayoutProps = HostelMessageProps & {
  message: React.ReactNode
  linkText: React.ReactNode
}

const HostelsAlertWrapper = styled('div')`
  padding: 0 0 ${cssTheme.layout.spacing.s600} 0;
`

const ExcludeHostelsMessage = ({onClick}: HostelMessageProps) => {
  return (
    <MessageLayout
      message={
        <FormattedMessageWrapper
          id="resultsIncludeHostels"
          defaultMessage="Your results include hostel rooms too."
        />
      }
      linkText={
        <FormattedMessageWrapper
          id="excludeHostels"
          defaultMessage="Exclude hostels"
        />
      }
      onClick={onClick}
    />
  )
}

const IncludeHostelsMessage = ({onClick}: HostelMessageProps) => {
  return (
    <MessageLayout
      message={
        <FormattedMessageWrapper
          id="resultsHaveNoHostels"
          defaultMessage="You have excluded hostels from results."
        />
      }
      linkText={
        <FormattedMessageWrapper
          id="includeHostels"
          defaultMessage="Include hostels"
        />
      }
      onClick={onClick}
    />
  )
}

const MessageLayout = ({message, linkText, onClick}: MessageLayoutProps) => {
  return (
    <Grid container alignItems="center" spacing="s200">
      <Grid mobileSm={12} desktopXs="fit">
        {message}
      </Grid>
      <Grid mobileSm={12} desktopXs="fill">
        <ButtonLink isCompact isBold={false} onClick={onClick}>
          {linkText}
        </ButtonLink>
      </Grid>
    </Grid>
  )
}

export const HostelsAlert = ({onChange}: Props) => {
  const dispatch = useDispatch()
  const noHostels = useSelector(getNoHostels)
  const isSortPriceAscending = useSelector(isSortAscendingByPrice)
  const showHostelsAlert = useSelector(getShowHostelsAlert)
  const isUserClosedHostelAlert = useSelector(getIsUserClosedHostelAlert)

  useEffect(() => {
    if (!isSortPriceAscending) {
      dispatch(setIsUserClosedHostelAlert(false))
    }
  }, [isSortPriceAscending, dispatch])

  const handleExcludeClick = (event: React.MouseEvent) => {
    event.preventDefault()

    onChange({noHostels: '1', userSearch: '1'})
  }

  const handleIncludeClick = (event: React.MouseEvent) => {
    event.preventDefault()

    onChange({noHostels: undefined, userSearch: '1'})
  }

  const handleCloseClick = () => {
    dispatch(setIsUserClosedHostelAlert(true))
  }

  if (showHostelsAlert && !isUserClosedHostelAlert) {
    return (
      <OpacityAnimation delay={1500} duration={0.5}>
        <HostelsAlertWrapper>
          <Alert icon={<Icon name="Information" />} onClose={handleCloseClick}>
            {noHostels ? (
              <IncludeHostelsMessage onClick={handleIncludeClick} />
            ) : (
              <ExcludeHostelsMessage onClick={handleExcludeClick} />
            )}
          </Alert>
        </HostelsAlertWrapper>
      </OpacityAnimation>
    )
  }

  return null
}
